/**
 * Module dependencies.
 */

import { Button } from 'app/components/atoms/button/button';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CrudTemplate } from '../form-types';
import styles from '../crud.module.less';
import { FiltersForm } from './filters-form';
import { resolveAppPath } from 'app/core/utils/url-resolver';
import { ExportButton } from 'app/components/molecules/export-button';

/**
 * `Props` type.
 */

type Props<T> = {
  template: CrudTemplate<T>;
  appendFilters: (values: any) => void;
  refresh: () => Promise<any>;
  params: any;
  hasNested: boolean;
  children?: JSX.Element | null;
};

/**
 * Export `CrudFilters` component.
 */

export function CrudFilters<T>(props: Props<T>): JSX.Element {
  const { children, template, hasNested, refresh, appendFilters, params } = props;
  const list = useMemo(() => template?.list, [template?.list]);
  const [searchParams] = useSearchParams();
  const filters = useMemo(() => list?.filters ?? [], [list?.filters]);
  const handleAppendFilters = useCallback(
    (data: any) => {
      // Remove pagination parameters from filters as they are handled separately
      if (data.filters) {
        const { page: _page, pageSize: _pageSize, order: _order, ...actualFilters } = data.filters;
        data = {
          ...data,
          filters: actualFilters
        };
      }

      const normalized = template.list?.normalizeFilters?.(data, params);
      appendFilters(normalized ?? data);
    },
    [appendFilters, params, template.list]
  );

  // Extract resource name from endpoint
  const resourceName = useMemo(() => {
    if (!template?.list?.enableExport || !template?.list?.endpoint) {
      return null;
    }

    const endpoint = typeof template.list.endpoint === 'string' ? template.list.endpoint : template.list.endpoint;

    // Extract last segment from path (e.g., '/admin/users' -> 'users')
    const segments = endpoint.split('/').filter(Boolean);
    return segments[segments.length - 1];
  }, [template?.list?.enableExport, template?.list?.endpoint]);

  // Convert searchParams to object for export
  // We need to pass search params as-is to maintain the URL query format
  const queryParams = useMemo(() => {
    const params: Record<string, any> = {};
    searchParams.forEach((value, key) => {
      // Skip non-filter parameters that are UI-only
      if (key === 'filterOpened') return;
      params[key] = value;
    });
    return params;
  }, [searchParams]);

  useEffect(() => {
    if (hasNested) {
      appendFilters({});
    }
  }, [appendFilters, hasNested]);

  return (
    <div className={styles.header}>
      <FiltersForm
        appendFilters={handleAppendFilters}
        defaultQueryString={template?.list?.defaultQueryString}
        filters={filters}
        hasNested={hasNested}
        inlineFiltersExpanded={template?.list?.inlineFiltersExpanded}
        queryStorageKey={template?.list?.endpoint}
      >
        <div className={styles.headerActions}>
          {children}

          {template?.list?.extraActions?.({ refresh })}

          {template?.list?.enableExport && resourceName && (
            <ExportButton queryParams={queryParams} resource={resourceName} />
          )}

          {!!template?.add?.route && (
            <div className={styles.addButton}>
              <Link
                to={
                  template?.list?.interpolateAddLink?.(template, searchParams) ??
                  resolveAppPath(template?.add.route, params)
                }
              >
                <Button size={'large'} type={'primary'}>
                  {template.list?.addButton}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </FiltersForm>
    </div>
  );
}
